<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">{{ this.$route.name }}</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item active"><i v-bind:class="routeIcon"></i> {{ $t(this.$route.name) }}</li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">

        <div class="row col-lg-12">
          <div class="form-group col-auto">
            <EditForm :currency="{}" mode="create" v-on:finish="this.handleItemsUpdate">Create</EditForm>
          </div>
        </div>

        <advanced-filters-wrapper
          :filters="filters"
          :default-values="{}"
          :expanded="false"
          @onSearchClick="this.loadItems"
          @onResetClick="this.loadItems"
        ></advanced-filters-wrapper>

        <div class="row col-12" id="loading-container">
          <div class="col-12 mb-10">
            <div class="mb-10">
              <div class="scroll-wrapper">
                <div class="scroll">
                </div>
              </div>
              <div class="table-wrapper mb-10">
                <ve-table class="text-nowrap"
                          :columns="columns"
                          :table-data="items"
                          :sort-option="sortOption"
                          :row-style-option="{ stripe: true }"
                          :border-around="true"
                          :border-x="true"
                          :border-y="true"
                />

                <div class="row mt-10">
                  <div class="col-6">
                  </div>
                  <div class="col-6">
                    <ve-pagination
                      :layout="['total', 'prev', 'pager', 'next']"
                      :page-index="pagination.page"
                      :page-size="pagination.onPage"
                      :total="pagination.count"
                      class="float-right"
                      @on-page-number-change="onPageChange"
                    />
                  </div>
                </div>

                <div class="row mb-10">
                  <div class="col-12">
                    <ve-pagination
                      class="float-right"
                      :page-index="pagination.page"
                      :page-size="pagination.onPage"
                      :total="pagination.count"
                      :pagingCount="pagination.count"
                      :layout="['total', 'sizer']"
                      :pageSizeOption="pagination.pageSizeOption"
                      @on-page-size-change="onPageSizeChange"
                    />
                  </div>
                </div>

              </div>
              <div v-show="showEmpty" class="row mb-10 mt-10 empty-data">Empty results</div>
              <div v-show="showError" class="row mb-10 mt-10 empty-data">Failed</div>
            </div>

          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import axios from "axios";
import {getRouteIcon, ROUTE_USERS} from "../../utils/routerIcons";
import AdvancedFiltersWrapper from '../../components/AdvancedFiltersWrapper.vue';
import EditCurrencyForm from '../../components/Currency/EditCurrencyForm.vue';
import RemoveForm from '../../components/remove-form/RemoveForm.vue';
import * as CurrencyUpdateTypes from '../../definitions/CurrencyUpdateTypes'
import * as CurrencyTypes from '../../definitions/CurrencyTypes'
import * as CurrencyLimitUpdateTypes from '../../definitions/CurrencyLimitUpdateTypes'

function getDefaultPagination() {
  return {
    onPage: 25,
    page: 1,
    records: 0,
    count: 0,
    pageSizeOption: [25,50,100,200],
  };
}

function getDefaultSort() {
  return {
    column: 'code',
    order: 'ASC',
  };
}

export default {
  components: {
    AdvancedFiltersWrapper,
    EditForm: EditCurrencyForm,
  },
  data() {
    return {
      showEmpty: false,
      showError: false,
      cancel: null,
      filterValues: {},
      filters: [
        {
          placeholder: 'Code',
          label: 'Code',
          inputId: 'code',
          modelProp: 'code'
        },
        {
          placeholder: 'Select types',
          label: 'Types',
          type: 'multiselect',
          multiple: true,
          options: [{id: 2, name: 'Crypto'}, {id: 3, name: 'Fiat'}],
          trackBy: 'id',
          labelProp: 'name',
          inputId: 'types',
          modelProp: 'types'
        },
        {
          placeholder: 'Select update types',
          label: 'Update Types',
          type: 'multiselect',
          multiple: true,
          options: [{id: 0, name: 'Manual'}, {id: 1, name: 'Auto'}, {id: 2, name: 'Multiplier'}],
          trackBy: 'id',
          labelProp: 'name',
          inputId: 'updateTypes',
          modelProp: 'updateTypes'
        },
      ],
      columns: [
        {field: 'fullName', title: 'Currency', align: 'left', sortBy: false,},
        {field: 'code', title: 'Code', align: 'left', sortBy: '',},
        {field: 'toEUR', title: 'In Euro', align: 'left', sortBy: '',renderBodyCell: ({row}) => {
            return row.toEUR.toFixed(2);
          }},
        {field: 'updated', title: 'Last Update', align: 'left', sortBy: false,},
        {field: 'type', title: 'Type', align: 'left', sortBy: '', renderBodyCell: ({row}) => {
            return CurrencyTypes.TYPES[row.type] ?? row.type;
          }},
        {field: 'parentCode', title: 'Base currency', align: 'left', sortBy: false,},
        {field: 'updateType', title: 'Update Type', align: 'left', sortBy: '', renderBodyCell: ({row}) => {
            return CurrencyUpdateTypes.TYPES[row.updateType] ?? row.updateType;
          }},
        {field: 'limitUpdateType', title: 'Update Limit', align: 'left', sortBy: '', renderBodyCell: ({row}) => {
            const v = row.limitUpdateType;
            if (v === null || v === undefined) {
              return 'Manual';
            }
            return CurrencyLimitUpdateTypes.TYPES[v] ?? v;
          }},
        {field: 'fractionDigits', title: 'Fraction Digits', align: 'left', sortBy: false,},
        {field: 'actions', title: 'Actions', align: 'left', renderBodyCell: ({row}) => {return <span>
              <EditCurrencyForm currency={row} mode='edit' v-on:finish={this.handleItemsUpdate}></EditCurrencyForm>
              <RemoveForm object={row.code}
                          message="Please confirm removing currency"
                          url={'/api/v1/currencies/' + row.code}
                          v-on:finish={this.handleItemsUpdate}
              ></RemoveForm>
            </span>
          }
        },
      ],
      items: [],
      sortOption: {
        sortChange: (params) => {
          let sort = null;

          for (let i in params) {
            if (params[i] !== "") {
              sort = {
                column: i,
                order: params[i],
              };
            }
          }

          if (sort) {
            this.sorts = sort;
          } else {
            this.sorts = getDefaultSort();
          }

          this.page = 1;
          this.loadItems();
        },
      },
      sorts: getDefaultSort(),
      pagination: getDefaultPagination(),
    }
  },
  computed: {
    ...mapGetters([
    ]),
    routeIcon: () => getRouteIcon(ROUTE_USERS),
  },
  methods: {
    getFiltersSnapshot(filters) {
      return JSON.stringify({
        filters: filters,
        sorts: this.sorts,
      });
    },
    handleItemsUpdate() {
      this.loadItems();
    },
    loadItems(filters) {
      if (!filters) {
        filters = this.filterValues;
      }
      this.filterValues = filters;
      const filterSnapshot = this.getFiltersSnapshot(filters);

      if (this.lastResultFilterSnapshot !== filterSnapshot) {
        this.pagination.page = 1;
      }

      this.showEmpty = false;
      this.showError = false;

      let loadingInstance = this.$veLoading({
        target: document.querySelector("#loading-container"),
        name: "wave",
      });

      this.items = [];

      this.startedAt = new Date();
      this.finishedAt = null;

      let self = this;
      loadingInstance.show();

      this
          .$http({
            url: '/api/v1/currencies',
            params: {
              ...filters,

              // pagination
              limit: this.pagination.onPage,
              offset: this.pagination.onPage * (this.pagination.page - 1),

              // sort params
              orderBy: this.sorts.column,
              direction: this.sorts.order,
            },
            method: 'GET',
            cancelToken: new axios.CancelToken(function executor(c) {
              self.cancel = c;
            })
          })
          .then(resp => {
            let {items} = resp.data;

            this.items = items;
            this.pagination.onPage = resp.data.pagination.limit;
            this.pagination.count = resp.data.pagination.totalCount;

            if (this.items.length === 0) {
              this.showEmpty = true;
            }

            this.lastResultFilterSnapshot = filterSnapshot;
          })
          .catch((err) => {
            console.log(err);
            this.items = [];
            this.showError = true;
          })
          .finally(() => {
            loadingInstance.close();
            this.cancel = null;
          });
    },
    cancelRequest() {
      if (this.cancel) {
        this.cancel('this -> cancel previous request');
      }
    },
    onPageChange(page) {
      this.pagination.page = page;
      this.loadItems()
    },
    onPageSizeChange(pageSize) {
      this.pagination.page = 1;
      this.pagination.onPage = pageSize;
      this.loadItems();
    },
    onFinishEdit() {
      this.loadItems();
    },
  },
  mounted() {
    this.loadItems();
  },
  beforeDestroy() {
    this.cancelRequest();
  },
}
</script>
